import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { IoArrowUpCircleOutline } from "react-icons/io5";
import { BASEAPIURL, token } from '../env';

// Memoized component for individual committee member cards
const CommitteeMemberCard = React.memo(({ member, index }) => {
  // Memoize the year extraction logic
  const yearRange = useMemo(() => {
    const startYear = member.JoiningDate ? member.JoiningDate.split('T')[0].split('-')[0] : '';
    const endYear = member.Timestamp ? member.Timestamp.split('T')[0].split('-')[0] : '';
    return `${startYear}${endYear ? ` - ${endYear}` : ''}`;
  }, [member.JoiningDate, member.Timestamp]);

  // Memoize image source handling
  const imageSrc = useMemo(() => {
    if (!member.Photo) return ''; // Handle missing photo

    // If it's already a full data URL, use it
    if (member.Photo.startsWith('data:image')) {
      return member.Photo;
    }

    // If it's base64 without prefix, add it
    if (member.Photo.match(/^[A-Za-z0-9+/=]+$/)) {
      return `data:image/jpeg;base64,${member.Photo}`;
    }

    // Otherwise assume it's a URL
    return member.Photo;
  }, [member.Photo]);

  return (
    <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
      <div
        className="card rounded-0 border-0"
        style={{
          height: "520px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <img
          src={imageSrc}
          loading="lazy"
          alt={member.Name || "Committee Member"}
          className="card-img-top"
          style={{
            objectFit: "fill", // keep ratio
            height: "200px",
            width: "200px",
            margin: "0 auto",
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://via.placeholder.com/200x200?text=No+Image";
          }}
        />

        <div
          className="card-body text-center d-flex flex-column"
          style={{ flex: 1, justifyContent: "flex-start", gap: "0.5rem" }}
        >
          {/* Name (3 rows max) */}
          <h5
            className="card-title mb-1"
            style={{
              fontWeight: 500,
              fontSize: "1.2em",
              color: "#154360",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: "2.4em", // ~3 lines of text
            }}
          >
            {member.Name || "\u00A0"}
          </h5>

          {/* Designation (2 rows max) */}
          <p
            className="card-text mb-1"
            style={{
              fontSize: "1em",
              color: "#154360",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: "1.2em", // ~2 lines of text
            }}
          >
            {member.Designation || "\u00A0"}
          </p>

          {/* Address (as it is, single line or wrap) */}
          <p
            className="card-text"
            style={{
              fontSize: "0.9em",
              color: "#555",
              marginBottom: 0,
              whiteSpace: "normal", // allow wrapping
              minHeight: "1.2em",
            }}
          >
            {member.Address || "\u00A0"}
          </p>

          {/* Business Title (2 rows max) */}
          <p
            className="card-text mb-1 mt-2"
            style={{
              fontSize: "1.1em",
              fontWeight: 500,
              color: "#154360",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: "1.2em", // ~2 lines of text
            }}
          >
            {member.BusinessTitle || "\u00A0"}
          </p>
        </div>

      </div>


    </div>
  );
});

export default function ManagementCommittee() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);

  const fetchCommitteeMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BASEAPIURL}/GetManagementCommunity`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          tokenData: token,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setRowData(result.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching committee members:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    scrollToTop();
    fetchCommitteeMembers();
  }, [scrollToTop, fetchCommitteeMembers]);

  // Memoize the list of member cards
  const memberCards = useMemo(() => {
    if (!rowData || rowData.length === 0) return null;

    return rowData.map((member, index) => (
      <CommitteeMemberCard
        key={member.id || member.Name || index}
        member={member}
        index={index}
      />
    ));
  }, [rowData]);

  return (
    <div>
      <div className="container">
        {loading && (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading committee members...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger text-center" role="alert">
            Error: {error}
          </div>
        )}

        {!loading && !error && (
          <div className="row">
            {memberCards}
          </div>
        )}
      </div>

      <div
        className="scroll-to-top-button rounded-2"
        onClick={scrollToTop}
        style={{
          width: 40,
          height: 40,
          backgroundColor: "#ff68f0",
          color: "#fff",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          position: "fixed",
          bottom: 20,
          right: 20,
          cursor: "pointer",
          zIndex: 1000
        }}
      >
        <IoArrowUpCircleOutline style={{ fontSize: 30 }} />
      </div>
    </div>
  );
}